import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import { ColumnMode, SelectionType } from '@swimlane/ngx-datatable';
import { BehaviorSubject } from 'rxjs';

import { Tenant } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { MessageService } from 'src/services/message.service';
import { TenantsService } from 'src/services/tenants.service';

import { SharedTenantService } from './shared-tenant.service';

interface TaxonomyCategory {
  category: string;
  articleCount: number;
}

interface TaxonomyDimensionStats {
  dimensionId: string;
  dimensionName: string;
  strategy: string;
  totalArticles: number;
  classifiedArticles: number;
  progressPercent: number;
  categories: TaxonomyCategory[];
  isComplete: boolean;
}

interface TaxonomyStats {
  tenantId: string;
  dimensions: TaxonomyDimensionStats[];
  summary: {
    totalDimensions: number;
    activeDimensions: number;
    completedDimensions: number;
  };
}

interface StrategySettings {
  strategy: string;
  confidenceThresholds: {
    llm: number;
    centroid: number;
  };
  availableStrategies: string[];
  strategyDescriptions?: {
    [key: string]: string;
  };
}

interface TaxonomyResponse {
  taxonomyStats: TaxonomyStats;
  strategySettings: StrategySettings;
  meta: {
    description: string;
    lastUpdated: string;
  };
}

interface TaxonomyDimension {
  id: string;
  tenantId: string;
  projectId: string | null;
  name: string;
  role: string | null;
  strategy: string;
  llmThreshold: number;
  centroidThreshold: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListDimensionsResponse {
  dimensions: TaxonomyDimension[];
  meta: {
    total: number;
    active: number;
  };
}

@Component({
  selector: 'app-super-admin-taxonomy',
  templateUrl: './super-admin-taxonomy.component.html',
  styleUrls: ['./super-admin-taxonomy.component.scss'],
})
export class SuperAdminTaxonomyComponent implements OnInit {
  stats: TaxonomyResponse | null = null;
  dimensions: TaxonomyDimension[] = [];
  selectedDimension: TaxonomyDimension | null = null;
  pendingDimensionId: string | null = null;
  isLoading = false;
  isLoadingTenants = true;
  isLoadingDimensions = false;
  error: string | null = null;

  // Create dimension modal
  showCreateDimensionModal = false;
  newDimensionForm = {
    name: '',
    role: '',
    strategy: '',
  };

  // Tenant selection
  public readonly tenants$: BehaviorSubject<Partial<Tenant>[]> = new BehaviorSubject([]);
  public selectedTenant: Partial<Tenant> | null = null;
  public tenantSearchText: string = '';
  public ColumnMode = ColumnMode;
  public SelectionType = SelectionType;
  public selected = [];
  public tenantColumns = [
    { name: 'Name', prop: 'name', flexGrow: 2 },
    { name: 'ID', prop: 'id', flexGrow: 1 },
    { name: 'Subdomain', prop: 'subdomain', flexGrow: 1 },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private tenantsService: TenantsService,
    private sharedTenantService: SharedTenantService,
  ) {}

  async ngOnInit() {
    await this.loadTenants();

    // Check if tenant ID and dimension ID are in the URL
    this.route.paramMap.subscribe((params) => {
      const tenantId = params.get('tenantId');
      const dimensionId = params.get('dimensionId');

      if (tenantId && !this.selectedTenant) {
        this.selectTenantById(tenantId, dimensionId || undefined);
      } else if (tenantId && this.selectedTenant && tenantId !== this.selectedTenant.id) {
        // Tenant changed
        this.selectTenantById(tenantId, dimensionId || undefined);
      } else if (
        dimensionId &&
        this.selectedTenant &&
        (!this.selectedDimension || dimensionId !== this.selectedDimension.id)
      ) {
        // Dimension changed or needs to be selected
        this.selectDimensionById(dimensionId);
      } else if (!dimensionId && this.selectedDimension) {
        // Dimension removed from URL, clear selection
        this.selectedDimension = null;
        this.stats = null;
      }
    });
  }

  async loadTenants() {
    const res = await this.tenantsService.list({
      limit: null,
    });
    this.tenants$.next(res.data);
    this.isLoadingTenants = false;
  }

  onTenantSelect(event) {
    // Navigate immediately on selection (like super-admin-tenants)
    if (event.selected.length > 0) {
      const tenant = event.selected[0];
      this.selectTenant(tenant);
    }
  }

  selectTenant(tenant: Partial<Tenant>) {
    this.selectedTenant = tenant;
    this.selected = [tenant];

    // Update shared tenant service
    this.sharedTenantService.setSelectedTenant(tenant);

    // Update URL to include tenant ID
    this.router.navigate(['/super-admin/taxonomy', tenant.id], { replaceUrl: true });

    // Only load dimensions - stats are dimension-specific
    this.loadDimensions();
  }

  async selectTenantById(tenantId: string, dimensionId?: string) {
    // Find tenant in the list
    const tenants = this.tenants$.value;
    const tenant = tenants.find((t) => t.id === tenantId);

    if (tenant) {
      this.selectedTenant = tenant;
      this.selected = [tenant];

      // Update shared tenant service
      this.sharedTenantService.setSelectedTenant(tenant);

      // Only load dimensions - if dimensionId is provided, it will be selected after dimensions load
      this.loadDimensions();

      // Store dimension ID to select after dimensions load
      if (dimensionId) {
        this.pendingDimensionId = dimensionId;
      }
    } else {
      // Tenant not found in list, redirect back to tenant selection
      this.router.navigate(['/super-admin/taxonomy'], { replaceUrl: true });
    }
  }

  selectDimensionById(dimensionId: string) {
    const dimension = this.dimensions.find((d) => d.id === dimensionId);
    if (dimension) {
      this.selectDimension(dimension);
    }
  }

  clearTenantSelection() {
    // Get tenant ID before clearing selected tenant
    const tenantId = this.selectedTenant?.id;

    this.selectedTenant = null;
    this.selected = [];
    this.stats = null;
    this.dimensions = [];
    this.selectedDimension = null;
    this.pendingDimensionId = null;

    // Navigate back to dimensions list (tenant page without dimension selected)
    if (tenantId) {
      this.router.navigate(['/super-admin/taxonomy', tenantId], { replaceUrl: true });
    }
  }

  goToTenantHome() {
    if (!this.selectedTenant) return;

    // Clear dimension-specific data but keep tenant selected
    this.stats = null;
    this.selectedDimension = null;
    this.pendingDimensionId = null;

    // Navigate to tenant home (no dimension selected)
    this.router.navigate(['/super-admin/taxonomy', this.selectedTenant.id], { replaceUrl: true });
  }

  loadStats() {
    if (!this.selectedTenant || !this.selectedDimension) return;

    this.isLoading = true;
    this.error = null;
    this.stats = null;

    this.apiService.get<TaxonomyResponse>(`/knowledge-base/taxonomy/${this.selectedTenant.id}/tenant-stats`).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.stats = response;
      },
      error: (err) => {
        this.isLoading = false;
        this.error = 'Failed to load taxonomy stats: ' + (err.message || 'Unknown error');
      },
    });
  }

  onRefresh() {
    // Reload dimensions list
    this.loadDimensions();

    // If a dimension is selected, also reload its stats
    if (this.selectedDimension) {
      this.loadStats();
    }
  }

  getStatusColor(progressPercent: number): string {
    if (progressPercent >= 90) return 'success';
    if (progressPercent >= 50) return 'warning';
    return 'danger';
  }

  getStatusText(progressPercent: number): string {
    if (progressPercent >= 90) return 'Excellent';
    if (progressPercent >= 70) return 'Good';
    if (progressPercent >= 50) return 'Moderate';
    return 'Needs Attention';
  }

  trackByCategory(index: number, category: TaxonomyCategory): string {
    return category.category;
  }

  getStrategyLabel(strategy: string): string {
    const labels: { [key: string]: string } = {
      large: 'Large (LLM)',
      'small-llm': 'Small LLM',
      'small-centroid': 'Small Centroid',
      'small-hybrid': 'Small Hybrid',
    };
    return labels[strategy] || strategy;
  }

  getStrategyColor(strategy: string): string {
    const colors: { [key: string]: string } = {
      large: 'tertiary',
      'small-llm': 'primary',
      'small-centroid': 'success',
      'small-hybrid': 'warning',
    };
    return colors[strategy] || 'medium';
  }

  // Dimension Management Methods
  loadDimensions() {
    if (!this.selectedTenant) return;

    this.isLoadingDimensions = true;
    this.error = null;

    this.apiService
      .get<ListDimensionsResponse>(`/knowledge-base/taxonomy/${this.selectedTenant.id}/taxonomy-dimensions`)
      .subscribe({
        next: (response) => {
          this.isLoadingDimensions = false;
          this.dimensions = response.dimensions;

          // If there's a pending dimension ID from URL, select it
          if (this.pendingDimensionId) {
            const pendingDimension = this.dimensions.find((d) => d.id === this.pendingDimensionId);
            if (pendingDimension) {
              this.selectDimension(pendingDimension);
            }
            this.pendingDimensionId = null;
          }
        },
        error: (err) => {
          this.isLoadingDimensions = false;
          this.error = 'Failed to load taxonomy dimensions: ' + (err.message || 'Unknown error');
        },
      });
  }

  selectDimension(dimension: TaxonomyDimension) {
    this.selectedDimension = dimension;

    // Update URL to include dimension ID
    if (this.selectedTenant) {
      this.router.navigate(['/super-admin/taxonomy', this.selectedTenant.id, dimension.id], { replaceUrl: true });
    }

    // Load dimension-specific data
    this.loadStats();
  }

  async createDimension() {
    this.showCreateDimensionModal = true;
    // Reset form
    this.newDimensionForm = {
      name: '',
      role: '',
      strategy: '',
    };
  }

  cancelCreateDimension() {
    this.showCreateDimensionModal = false;
  }

  async confirmCreateDimension() {
    if (!this.newDimensionForm.strategy) {
      this.messageService.showError('Strategy is required');
      return;
    }

    // Generate name if not provided
    const finalName =
      this.newDimensionForm.name ||
      this.generateDimensionName(this.newDimensionForm.strategy, this.newDimensionForm.role);

    await this.performCreateDimension({
      name: finalName,
      role: this.newDimensionForm.role || null,
      strategy: this.newDimensionForm.strategy,
      llmThreshold: 0.5,
      centroidThreshold: 0.5,
    });

    this.showCreateDimensionModal = false;
  }

  private generateDimensionName(strategy: string, role?: string): string {
    const strategyNames = {
      large: 'Container Classification',
      'small-llm': 'Granular LLM Classification',
      'small-centroid': 'Centroid-based Classification',
      'small-hybrid': 'Hybrid Classification',
    };

    const baseName = strategyNames[strategy] || 'Taxonomy Classification';

    if (role) {
      const roleCapitalized = role.charAt(0).toUpperCase() + role.slice(1);
      return `${roleCapitalized} ${baseName}`;
    }

    return baseName;
  }

  private async performCreateDimension(dimensionData: any) {
    const loading = await this.loadingController.create({
      message: 'Creating dimension...',
    });
    await loading.present();

    try {
      await this.apiService.postPromise(
        `/knowledge-base/taxonomy/${this.selectedTenant?.id}/taxonomy-dimensions`,
        dimensionData,
      );

      await loading.dismiss();
      this.messageService.show('Dimension created successfully!');
      this.loadDimensions(); // Refresh dimensions
    } catch (error) {
      await loading.dismiss();
      this.messageService.showError('Failed to create dimension: ' + (error.message || 'Unknown error'));
    }
  }

  async toggleDimensionActive(dimension: TaxonomyDimension) {
    const action = dimension.isActive ? 'deactivate' : 'activate';
    const alert = await this.alertController.create({
      header: `${action.charAt(0).toUpperCase() + action.slice(1)} Dimension`,
      message: `Are you sure you want to ${action} the dimension "${dimension.name}"?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: action.charAt(0).toUpperCase() + action.slice(1),
          role: 'confirm',
          handler: () => {
            this.performToggleDimensionActive(dimension);
          },
        },
      ],
    });

    await alert.present();
  }

  private async performToggleDimensionActive(dimension: TaxonomyDimension) {
    const loading = await this.loadingController.create({
      message: `${dimension.isActive ? 'Deactivating' : 'Activating'} dimension...`,
    });
    await loading.present();

    try {
      await this.apiService.putPromise(
        `/knowledge-base/taxonomy/${this.selectedTenant?.id}/taxonomy-dimensions/${dimension.id}`,
        { isActive: !dimension.isActive },
      );

      await loading.dismiss();
      this.messageService.show(`Dimension ${dimension.isActive ? 'deactivated' : 'activated'} successfully!`);
      this.loadDimensions(); // Refresh dimensions
    } catch (error) {
      await loading.dismiss();
      this.messageService.showError(
        `Failed to ${dimension.isActive ? 'deactivate' : 'activate'} dimension: ` + (error.message || 'Unknown error'),
      );
    }
  }

  getDimensionStatusColor(dimension: TaxonomyDimension): string {
    return dimension.isActive ? 'success' : 'medium';
  }

  getDimensionStatusText(dimension: TaxonomyDimension): string {
    return dimension.isActive ? 'Active' : 'Inactive';
  }

  trackByDimension(index: number, dimension: TaxonomyDimension): string {
    return dimension.id;
  }
}
